import { goto } from '$app/navigation';
import { ApiService } from '$lib/services/api';
import {
	PAGES,
	GTM_EVENT_NAMES,
	COOKIES,
	COOKIES_CONSENT,
	LOCAL_STORAGE_KEYS,
	THIRD_PARTY_EVENTS,
	AOFL_PRODUCT_NAMES,
	SENTRY_ERROR_LEVELS,
	REG_PATH_STEPS,
} from '$lib/utils/constants';
import { get } from 'svelte/store';
import { UnknownError } from '../subscription/modules/errors';
import { publishFailure, publishSuccess, user, resetStore } from './store';
import { resetDesktopChoice } from '../app/actions';
import { loadingService } from '$lib/services/loading';
import { Sentry } from '$lib/services/sentry';
import Cookies from 'js-cookie';
import Logger from '$lib/services/analytics/events';
import { EventType } from '$lib/services/analytics/events/common';
import { getDeviceId } from '$lib/utils/device-id';
import { app } from '../app/store';
import { AnalyticsService } from '$lib/services/analytics';
import { v4 as uuidv4 } from 'uuid';
import { AppService } from '$lib/services/app';
import { ProductExperienceService } from '$lib/services/product-experience';
import { has1_0Account, has2_0Account } from '$lib/utils/aofl-accounts';
import { postAuthenticationPathService } from '$lib/services/post-authentication-path';
import { experimentService } from '$lib/services/experiments/client';

/**
 * @param {{ email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @param {{ redirect: boolean; logType?: EventType; postLoginRedirectPath?: string }} options - options to handle this action
 * @returns {Promise<{data: import('./store').UserStoreState; error?: { message: string }; playVoiceOver?: boolean}>} result
 */
export async function login(credentialInfo, options = { redirect: false }) {
	return loadingService.withLoading(async () => {
		try {
			const loginResp = await ApiService.login({
				username: credentialInfo.email,
				password: credentialInfo.password,
				country_abbrv: credentialInfo.countryAbbrv,
				username_type: credentialInfo.usernameType,
			});

			await AnalyticsService.dataLayerPush({
				eventName: GTM_EVENT_NAMES.LOGIN,
				email: credentialInfo?.email,
			});

			const newUserState = {
				...loginResp,
				password: credentialInfo.password,
				token_valid: true,
			};
			publishSuccess(newUserState);

			if (has2_0Account()) {
				// Mixed users or 2.0 pure users should always default to a 2.0 experience.
				ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE_2_0);
			} else {
				ProductExperienceService.setProductExperience(AOFL_PRODUCT_NAMES.ABCMOUSE);
			}

			await experimentService.initializeExperiments();

			// Get the user object from the latest store state
			const getUser = get(user);

			if (options.logType) logEvent(options.logType, getUser);
			let playVoiceOver = false;
			if (options.redirect) {
				const has_1_0_account = has1_0Account();
				const has_2_0_account = has2_0Account();

				// Play the update voice over for mixed 1.0 and 2.0 users
				playVoiceOver = has_1_0_account && has_2_0_account;

				const defaultPath = postAuthenticationPathService.getRedirectPath(getUser);
				const path = options.postLoginRedirectPath ?? defaultPath;

				playVoiceOver = playVoiceOver && path === PAGES.INTERNAL.DOWNLOAD_APP;

				// Redirect to the path. Cross-app targets (e.g. /parent-center) live
				// outside this app's `/abc` base; goto only handles in-app routes, so
				// use a full document navigation for them.
				// Anything that is not /abc is not part of the client and therefore requires a full document navigation.
				if (path.startsWith('/abc')) {
					await goto(path);
				} else {
					window.location.assign(path);
				}
			}

			return { data: getUser, error: null, playVoiceOver: playVoiceOver };
		} catch (error) {
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			publishFailure(er);

			// Get the user object from the latest store state
			const getUser = get(user);

			return { data: getUser, error: er, playVoiceOver: false };
		}
	});
}

/**
 * @typedef {{ type: 'magic_link'; magic_link_id?: string } | { type: 'password' }} PreLoginResult
 */

/**
 * Calls the preLogin API and returns the resolved next step.
 * Throws APIError on network/API failures (including MAX_ATTEMPTS).
 * @param {{ username: string }} username - The user's email as username to pre-login
 * @returns {Promise<PreLoginResult>}
 */
export async function preLogin({ username }) {
	const response =
		/** @type {{ success?: boolean; next_step_type?: string; next_step_data?: { magic_link_id?: string } }} */ (
			await ApiService.preLogin({ username })
		);
	if (response?.success === false) {
		throw new Error('PreLogin invalid');
	}
	if (response?.next_step_type === 'password') {
		return { type: 'password' };
	}
	if (response?.next_step_type !== 'magic_link') {
		throw new Error('PreLogin invalid');
	}
	return { type: 'magic_link', magic_link_id: response?.next_step_data?.magic_link_id };
}

/**
 * @param {{ username: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @returns {Promise<object | unknown>} result
 */
export async function checkUserStatusWithCredentials(credentialInfo) {
	try {
		const userStatusResp = await ApiService.userGetStatus({
			username: credentialInfo.username,
			password: credentialInfo.password,
			country_abbrv: credentialInfo.countryAbbrv,
			username_type: credentialInfo.usernameType,
		});

		const newUserState = { ...userStatusResp };
		publishSuccess(newUserState);
		return get(user);
	} catch (error) {
		// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
		Sentry.captureMessage('UserAction: Failed to check user status with credentials', {
			extra: {
				error: error,
			},
		});
		if (!(error instanceof Error)) {
			publishFailure(new UnknownError(error));
		} else {
			publishFailure(error);
		}
	}
}

/**
 * Validates the current user session and updates the user store.
 * Always calls ApiService.validateLogin() to get fresh user state.
 *
 * Use this when you need to refresh/validate user state (e.g., after mutations).
 * To read current state without validating, use get(user) directly.
 *
 * Errors are caught internally and logged to Sentry. Does not throw or return anything.
 * @param {object} options - Options for the validate user call
 * @param {boolean} options.preserveSessionDeviceId - Whether to preserve the session device id (default: true)
 * @param {string} [options.token] - The token to validate
 * @returns {Promise<void>}
 * TODO: Clean up preserveSessionDeviceId option implementation
 */
export async function validateUser(options = {}) {
	try {
		const response = await ApiService.validateLogin(options?.token, undefined, options);

		const newUserState = {
			...response,
		};
		publishSuccess(newUserState);

		// Store aofl_token as source of truth for sessions and authentication
		const aoflToken = response?.aofl_token;
		if (aoflToken) {
			ApiService.setToken(aoflToken);
		} else {
			// Log fatal error if aofl_token is missing (should always be present for authenticated sessions)
			Sentry.captureMessage('UserAction: validateUser returned no session token', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: new Error('aofl_token missing from validateLogin response'),
					response: response,
				},
			});
		}
	} catch (error) {
		Sentry.captureMessage('UserAction: validateUser failed', {
			level: SENTRY_ERROR_LEVELS.FATAL,
			extra: {
				error: error,
			},
		});

		if (!(error instanceof Error)) {
			publishFailure(new UnknownError(error));
		} else {
			publishFailure(error);
		}
	}
}

/**
 * Exchanges a short-life token for a new long term token
 * @param {string} token - the token to introspect
 * @param {string} source - the source of the token
 * @returns {Promise<{ response: object | null; error: Error | unknown }>} the introspect response, null on failure
 */
export async function tokenIntrospect(token, source) {
	try {
		const response = await ApiService.tokenIntrospect({
			token: token,
			product_uuid: ProductExperienceService.AOFL_PRODUCT_UUID,
		});

		// Store aofl_token as source of truth for sessions and authentication
		const aoflToken = response?.aofl_token;
		if (aoflToken) {
			ApiService.setToken(aoflToken);
		} else {
			// Log fatal error if aofl_token is missing (should always be present after successful introspection)
			Sentry.captureMessage('UserAction: tokenIntrospect returned no session token', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: new Error('aofl_token missing from tokenIntrospect response'),
					response: response,
					source: source,
				},
			});
		}

		return { response, error: null };
	} catch (error) {
		Sentry.captureMessage('UserAction: tokenIntrospect failed', {
			level: SENTRY_ERROR_LEVELS.FATAL,
			extra: {
				error: error,
			},
		});
		return { response: null, error: error };
	}
}

/**
 * Resets the cookies and local storage
 * @returns {void}
 */
const resetCookiesAndLocalStorage = () => {
	const experiments = localStorage.getItem(LOCAL_STORAGE_KEYS.EXPERIMENTS);
	const aoflToken = localStorage.getItem(LOCAL_STORAGE_KEYS.AOFL_TOKEN);

	Object.keys(Cookies.get()).forEach((cookieName) => {
		if (cookieName === COOKIES.DEVICE_ID) {
			return;
		}
		Cookies.remove(cookieName);
	});

	// Remove token explicitly before clear so other tabs receive a key-specific
	// storage event (key='aofl_token', newValue=null) and can detect the logout.
	// localStorage.clear() only fires key=null, which the cross-tab listener cannot match.
	localStorage.removeItem(LOCAL_STORAGE_KEYS.AOFL_TOKEN);
	localStorage.clear();

	// Restore backed up values
	if (experiments) {
		localStorage.setItem(LOCAL_STORAGE_KEYS.EXPERIMENTS, experiments);
	}
	if (aoflToken) {
		ApiService.setToken(aoflToken);
	}

	resetStore();
	resetDesktopChoice();
};

/**
 * Logs out the user
 * @param {import('$lib/stores/app/types').AppStore} appStore - appStore
 * @param {{ redirect: boolean }} options - options
 * @returns {Promise<void>} result
 */
export async function logout({ meta }, options = { redirect: true }) {
	return loadingService.withLoading(async () => {
		try {
			// Logout user first (requires session/cookies to be present)
			const response = await ApiService.userLogout();

			if (meta.inApp) {
				const appCallService = new AppService();
				await appCallService.logout();
			}
			// Extract aofl_token before clearing storage
			const aoflToken = response?.aofl_token;

			// Clear cookies and localStorage after logout
			resetCookiesAndLocalStorage();

			// Store aofl_token as source of truth for sessions and authentication
			// (set after clear so it persists)
			if (aoflToken) {
				ApiService.setToken(aoflToken);
			} else {
				// Log fatal error if aofl_token is missing (should always be present after logout)
				Sentry.captureMessage('UserAction: logout returned no session token', {
					level: SENTRY_ERROR_LEVELS.FATAL,
					extra: {
						error: new Error('aofl_token missing from logout response'),
						response: response,
					},
				});
			}
		} catch (error) {
			Sentry.captureMessage('UserAction: logout failed', {
				level: SENTRY_ERROR_LEVELS.FATAL,
				extra: {
					error: error,
				},
			});
		} finally {
			if (options.redirect) await goto(PAGES.INTERNAL.LOGIN);
		}
	});
}

/**
 * Submits the users email
 * @param {{ email: string; countryAbbrv: string; usernameType: string; phone?: any; }} registerInfo - credentials
 * @param {{ redirect: boolean }} options - options to handle this action
 * @returns {Promise<object | unknown>} response
 */
export const registerEmail = async (registerInfo, options = { redirect: false }) => {
	return loadingService.withLoading(async () => {
		try {
			// TODO: finish all entries like types and campaign tag.
			const response = await ApiService.prospectRegisterEmail({
				email_address: registerInfo?.email,
				prospect_type: '',
				language_locale: get(app).meta.localeCode,
				campaign_source_tag: '',
				registration_type: registerInfo?.regType,
				subscription_status: '',
				phone_number: registerInfo?.phone,
			});

			// TODO: handle storing email in sessionStorage
			sessionStorage.setItem('tmpEmail', registerInfo.email);
			const newUserState = { tmpEmail: registerInfo.email };
			publishSuccess(newUserState);

			// TODO: move this to a transport, combine with datalayer push
			const sourceInfoJson = localStorage.getItem(LOCAL_STORAGE_KEYS.SOURCE_INFO) ?? '{}';
			const sourceInfo = JSON.parse(sourceInfoJson) || {};
			const conversionEventId = uuidv4();
			const eventSource = sourceInfo?.id ? [sourceInfo?.id] : [];
			await AnalyticsService.trackThirdPartyConversion({
				conversion_event_id: conversionEventId,
				country_code: registerInfo?.countryAbbrv,
				email: registerInfo?.email,
				event_info: {
					...(sourceInfo?.values ? sourceInfo?.values : {}),
					fbc: Cookies.get(COOKIES.FBC),
					fbp: Cookies.get(COOKIES.FBP),
					analytics_cookies_enabled: Cookies.get(COOKIES_CONSENT.ANALYTICS) === 'true',
					targeting_cookies_enabled: Cookies.get(COOKIES_CONSENT.TARGETING) === 'true',
				},
				event_source: eventSource,
				event_type: THIRD_PARTY_EVENTS.INITIATE_CHECKOUT,
			});
			await AnalyticsService.dataLayerPush({
				event_id: conversionEventId,
				eventName: GTM_EVENT_NAMES.PROSPECT_REG_SUBMITTED,
				email: registerInfo?.email,
				espRecipientID: response.esp_recipient_id,
				fbc: Cookies.get(COOKIES.FBC),
				fbp: Cookies.get(COOKIES.FBP),
			});

			return response;
		} catch (error) {
			// TODO: Monitor error in Sentry, see if this is a good place to log error, or if its too noisy
			Sentry.captureMessage('UserAction: registerEmail failed', {
				extra: {
					error: error,
				},
			});
			console.error(error);
		} finally {
			if (options.redirect) {
				goto(PAGES.INTERNAL.SUBSCRIPTION);
			}
		}
	});
};

/**
 * @param {EventType} eventType - type of event
 * @param {import('./store').UserStoreState} userInfo - the user state
 * @returns {Promise<object>}
 */
async function logEvent(eventType, userInfo) {
	return await Logger.log({
		type: eventType,
		deviceId: getDeviceId(),
		legacyTrackCookie: Cookies.get(COOKIES.TRACK_COOKIE),
		meta: get(app).meta,
		aoflProductId: ProductExperienceService.AOFL_PRODUCT_UUID,
		detail: {
			firstTimeLogin: userInfo.isFirstLogin,
			userToProductId: userInfo.userToProductUuid,
		},
	});
}

/**
 * Resets the user's password.
 * @param {{ token: string; email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @param {{ redirect?: boolean | string; logType?: EventType; logOutOfDevices?: boolean; aoflProductUuid?: string; }} options - options to handle this action. If `redirect` is `true`, navigates to `PAGES.INTERNAL.LOGIN`. If a `string`, navigates to that path.
 * @returns {Promise<{data: import('./store').UserStoreState; error?: object}>} result
 */
export async function userResetPassword(
	credentialInfo,
	{
		redirect = false,
		logOutOfDevices = false,
		aoflProductUuid = ProductExperienceService.AOFL_PRODUCT_UUID,
	} = {}
) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.userResetPassword(
				credentialInfo,
				logOutOfDevices,
				aoflProductUuid
			);

			if (redirect) {
				const destination = typeof redirect === 'string' ? redirect : PAGES.INTERNAL.LOGIN;
				await goto(destination);
			}
			return { data: response, error: undefined };
		} catch (error) {
			Sentry.captureMessage('UserAction: resetPassword failed', {
				extra: {
					error: error,
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			publishFailure(er);
			return { data: get(user), error: er };
		}
	});
}

/**
 * @param {object} params - The forgot password parameters
 * @param {string} params.email - The user's email address
 * @param {string} params.usernameType - The type of username
 * @param {string} params.countryAbbrv - The country abbreviation
 * @param {string} params.aoflProductUuid - The aofl product uuid
 * @returns {Promise<object>} - The API response
 */
export async function userForgotPassword({ email, usernameType, countryAbbrv, aoflProductUuid }) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.userForgotPassword({
				username_type: usernameType,
				username: email,
				country_abbrv: countryAbbrv,
				aofl_product_uuid: aoflProductUuid,
			});

			return response;
		} catch (error) {
			Sentry.captureMessage('UserAction: forgotPassword failed', {
				extra: {
					error: error,
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			publishFailure(er);
			return { data: get(user), error: er };
		}
	});
}

/**
 * Gets the user's latest payment information
 * @returns {Promise<{
 *   data: {
 *     isValidPaymentInfo: boolean,
 *     paymentUuid: string,
 *     paymentProvider: string,
 *     paymentProviderInfo: {
 *       ccLastFour: string,
 *       cardBrand: string,
 *       cardExpirationDate: string,
 *       paymentUuid: string
 *     }
 *   },
 *   error: Error | null
 * }>} Payment info or error
 */
export async function getPaymentInfo() {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.getPaymentInfo();

			const data = response.is_valid_payment_info
				? {
						isValidPaymentInfo: response.is_valid_payment_info,
						paymentUuid: response.payment_uuid,
						paymentProvider: response.payment_provider,
						paymentProviderInfo: {
							ccLastFour: response.payment_provider_info.cc_last_four,
							cardBrand: response.payment_provider_info.card_brand,
							cardExpirationDate: response.payment_provider_info.card_expiration_date,
							paymentUuid: response.payment_provider_info.payment_uuid,
						},
					}
				: { isValidPaymentInfo: response.is_valid_payment_info };

			return { data, error: null };
		} catch (error) {
			Sentry.captureMessage('UserAction: getPaymentInfo failed', {
				extra: {
					error: error,
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { error: er, data: null };
		}
	});
}

/**
 * Updates user credentials (email and/or password).
 * @param {{ token: string; username?: string; new_password?: string; old_password?: string; product_uuid?: string; }} credentials - The credential update object
 * @returns {Promise<{data: object; error: Error | null}>} result
 */
export async function updateUserCredentials(credentials) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.updateUserCredentials(credentials);

			// Update the user store with the new masked email if provided
			if (response?.masked_email) {
				const currentUser = get(user);
				publishSuccess({
					...currentUser,
					maskedEmail: response.masked_email,
				});
			}

			return { data: response, error: null };
		} catch (error) {
			Sentry.captureMessage('UserAction: updateCredentials failed', {
				extra: {
					error: error,
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			publishFailure(er);
			return { data: get(user), error: er };
		}
	});
}

/**
 * Validates a recovery token.
 * @param {string} token - The token to validate.
 * @returns {Promise<object>} A promise that resolves with the result of the validation.
 */
export async function validateRecoveryToken(token) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.userValidateRecoveryToken(token);
			return { data: response, error: undefined };
		} catch (error) {
			Sentry.captureMessage('UserAction: validateRecoveryToken failed', {
				extra: {
					error: error,
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { data: get(user), error: er };
		}
	});
}

/**
 * Gets the recipient email info.
 * @param {{ email_address: string; }} payload - The payload.
 * @returns {Promise<object>} A promise that resolves with the recipient email info.
 */
export async function getRecipientEmailInfo({ email_address }) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.recipientGetEmailInfo({ email_address });
			return { data: response, error: undefined };
		} catch (error) {
			Sentry.captureMessage('UserAction: getRecipientEmailInfo failed', {
				extra: {
					error: error,
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { data: get(user), error: er };
		}
	});
}

/**
 * @typedef {object} TokenCreateResponse
 * @property {string} [token] - The short-lived authentication token
 */

/**
 * Creates a short-lived token from an existing authenticated session.
 * @param {string} productUuid - The product uuid to create the token for.
 * @returns {Promise<{error: Error | null; data: TokenCreateResponse | null;}>} Token response or error
 */
export async function tokenCreate(productUuid) {
	try {
		const response = await ApiService.tokenCreate(productUuid);
		return { data: response, error: null };
	} catch (error) {
		Sentry.captureMessage('UserAction: createToken failed', {
			extra: {
				error: error,
			},
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { error: er, data: null };
	}
}

/**
 * @typedef {object} DeviceSessionResponse
 * @property {string} [token] - The authentication token
 * @property {string} [user_to_product_uuid] - The user to product UUID
 */

/**
 * Creates a user tied to device id for initial device authentication.
 * Used when no existing authentication is available.
 * @returns {Promise<{error: Error | null; data: DeviceSessionResponse | null;}>} Device session info or error
 */
export async function createDeviceUser() {
	try {
		const deviceId = getDeviceId();
		const response = await ApiService.createDeviceSession(deviceId);
		const validateLoginResponse = await ApiService.validateLogin(response.token);
		publishSuccess({ ...validateLoginResponse });
		return { data: response, error: null };
	} catch (error) {
		Sentry.captureMessage('UserAction: createDeviceSession failed', {
			extra: {
				error: error,
			},
		});
		let er = !(error instanceof Error) ? new UnknownError(error) : error;
		return { error: er, data: null };
	}
}

/**
 * Adds credential to existing account.
 * @param {object} credentials - Credentials to add on account
 * @param {string} token - Token
 * @param {string} aoflProductUuid  - aoflProductUuid
 * @returns {Promise<object>} Add credentials result
 */
export async function addCredentials(credentials, token, aoflProductUuid) {
	return await ApiService.addCredentials(credentials, token, aoflProductUuid);
}

/**
 * Generates a short-lived for the target product
 * @param {string} productUuid - The product uuid to create the token for.
 * @returns {Promise<string>} The short lived token
 */
export async function generateShortLivedToken(productUuid) {
	try {
		const response = await ApiService.tokenCreate(productUuid);
		return response.token;
	} catch (error) {
		Sentry.captureMessage('UserAction: generateShortLivedToken failed', {
			extra: {
				error: error,
			},
		});
		return '';
	}
}

/**
 * Adds credential to existing account.
 * @param {string} password - Password
 * @returns {Promise<object>} Upgrade request result or error
 */
export async function authenticatePassword(password) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.authenticatePassword(password);
			return { data: response, error: null };
		} catch (error) {
			Sentry.captureMessage('UserAction: authenticatePassword failed', {
				extra: {
					error: error,
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { data: get(user), error: er };
		}
	});
}

/**
 * Adds credential to existing account.
 * @param {string} productUuid - Product uuid
 * @param {string} planHash - Plan hash
 * @returns {Promise<object>} Upgrade request result or error
 */
export async function hasSubscribed(productUuid, planHash) {
	return loadingService.withLoading(async () => {
		try {
			const response = await ApiService.hasSubscribed(productUuid, planHash);
			return { data: response, error: null };
		} catch (error) {
			Sentry.captureException(error, {
				extra: {
					action: 'Error: upgradeRequest Action',
				},
			});
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			return { data: get(user), error: er };
		}
	});
}

/**
 * Takes a 2.0 pure user and adds a 1.0 account to mix the user.
 * This is temporary workaround until we have a more complete solution for single membership.
 * @param {{ email: string; password: string; countryAbbrv: string; usernameType: string; }} credentialInfo - credentials
 * @param {{ redirect?: boolean }} [options] - options
 * @returns {Promise<{data: import('./store').UserStoreState; error?: object}>} result
 */
export async function createMixedUser(credentialInfo, options = {}) {
	return loadingService.withLoading(async () => {
		try {
			await ApiService.createMixedUser({
				username: credentialInfo.email,
				password: credentialInfo.password,
				country_abbrv: credentialInfo.countryAbbrv,
				username_type: credentialInfo.usernameType,
			});

			// We call validate login to update the user state with the new mixed account info.
			const response = await ApiService.validateLogin();

			const newUserState = {
				...response,
			};
			publishSuccess(newUserState);

			if (options.redirect) {
				const path = postAuthenticationPathService.getRedirectPath(newUserState);
				await goto(path);
			}
		} catch (error) {
			let er = !(error instanceof Error) ? new UnknownError(error) : error;
			publishFailure(er);

			// Get the user object from the latest store state
			const getUser = get(user);

			return { data: getUser, error: er };
		}
	});
}

/**
 * Marks the user's registration path as complete if not already done.
 * Skips the API call when completed_step is already REGPATH_COMPLETE.
 * @returns {Promise<boolean | null>} true if the API call was made successfully
 * null if the user has already completed the reg path
 * false if the API call failed
 */
export async function markRegPathComplete() {
	try {
		const { completed_step } = get(user).userMetaData ?? {};
		if (completed_step === REG_PATH_STEPS.COMPLETE) return null;
		await ApiService.postCompletedStep({ completed_step: REG_PATH_STEPS.COMPLETE });
		return true;
	} catch (error) {
		Sentry.captureMessage('UserAction: regPathComplete failed', {
			extra: {
				error: error,
			},
		});
		return false;
	}
}
